export const ITEM_NUMBER_LENGTH = 8;
export const ITEM_NUMBER_PATTERN = /^\d{8}$/;
export const ACTIVE_ITEM_PREFIXES = ['ADS', 'ART', 'FCO', 'SGR', 'SPR'] as const;
const ITEM_PREFIXES_PATTERN = new RegExp(`\\b(${ACTIVE_ITEM_PREFIXES.join('|')})-?`, 'gi');
const DELIMITER_PATTERN = /[\s,;|.\n\r]+/;

export interface ItemNumberValidationResult {
   valid: string[];
   invalid: string[];
   duplicatesRemoved: number;
}

interface ExtractedNumbers {
   valid: string[];
   invalid: string[];
}

function extractNumbersFromSegment(segment: string): ExtractedNumbers {
   const valid: string[] = [];
   const invalid: string[] = [];

   const withoutDashes = segment.replace(/-/g, '');
   if (ITEM_NUMBER_PATTERN.test(withoutDashes)) {
      return { valid: [withoutDashes], invalid };
   }

   const numbers = segment.match(/\d+/g) ?? [];
   for (const num of numbers) {
      (num.length === ITEM_NUMBER_LENGTH ? valid : invalid).push(num);
   }

   return { valid, invalid };
}

function parseSegments(input: string): string[] {
   if (!input || typeof input !== 'string') return [];
   return input.toUpperCase().replace(ITEM_PREFIXES_PATTERN, '').split(DELIMITER_PATTERN).filter(Boolean);
}

export function formatItemNumbers(input: string): string[] {
   const seen = new Set<string>();

   for (const segment of parseSegments(input)) {
      extractNumbersFromSegment(segment).valid.forEach((n) => seen.add(n));
   }

   return [...seen];
}

export function validateItemNumberInput(input: string): ItemNumberValidationResult {
   const valid: string[] = [];
   const invalid: string[] = [];
   const seenValid = new Set<string>();
   const seenInvalid = new Set<string>();
   let totalValidCount = 0;

   for (const segment of parseSegments(input)) {
      const extracted = extractNumbersFromSegment(segment);

      for (const num of extracted.valid) {
         totalValidCount++;
         if (!seenValid.has(num)) {
            seenValid.add(num);
            valid.push(num);
         }
      }

      for (const num of extracted.invalid) {
         if (!seenInvalid.has(num)) {
            seenInvalid.add(num);
            invalid.push(num);
         }
      }
   }

   return { valid, invalid, duplicatesRemoved: totalValidCount - valid.length };
}

export const isValidItemNumber = (itemNumber: string): boolean => ITEM_NUMBER_PATTERN.test(itemNumber);

export const formatItemNumbersForDisplay = (itemNumbers: string[]): string => itemNumbers.join(', ');
